import { useEffect, useLayoutEffect, useState } from 'react';
import throttle from 'lodash/throttle';
import { useTouch } from './useTouch';
import {
  MEDIUM_SCREEN_WIDTH,
  NARROW_SCREEN_WIDTH,
  SMALL_SCREEN_WIDTH,
} from '@/app/constants';

const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
};

/**
 * Returns the current window dimensions, throttled to 200ms.
 */
export const useWindowDimensions = () => {
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );

  useEffect(() => {
    const handleResize = throttle(() => {
      setWindowDimensions(getWindowDimensions());
    }, 200);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
};

const useViewportBelow = (threshold: number) => {
  const [isBelow, setIsBelow] = useState(false);
  useLayoutEffect(() => {
    const update = () => setIsBelow(window.innerWidth < threshold);
    window.addEventListener('resize', update);
    update();
    return () => window.removeEventListener('resize', update);
  }, [threshold]);
  return isBelow;
};

/** True when the viewport is narrower than SMALL_SCREEN_WIDTH (1100px). */
export const useSmallScreen = () => useViewportBelow(SMALL_SCREEN_WIDTH);

/** True when the viewport is narrower than NARROW_SCREEN_WIDTH (1180px). */
export const useNarrowScreen = () => useViewportBelow(NARROW_SCREEN_WIDTH);

/** True when the viewport is narrower than MEDIUM_SCREEN_WIDTH (1300px). */
export const useMediumScreen = () => useViewportBelow(MEDIUM_SCREEN_WIDTH);

/**
 * If the screen is smaller than 1100px, the width is greater
 * than the height, and is touch, returns true
 */
export const useTablet = () => {
  const { height, width } = useWindowDimensions();
  const isTouchDevice = useTouch();
  const isSmallScreen = useSmallScreen();

  return !isSmallScreen && width > height && isTouchDevice;
};
